// RC7 Briefing Engine · Dedicated Weekly Briefing reader (PO 2026-02-11).
//
// Route: `briefing:<id>` (registered in App.js).
//
// The reader is deliberately editorial. Sections that carry no
// content are omitted — never rendered empty, never fabricated. The
// original `external_url` (legacy ChatGPT link) is intentionally NOT
// the primary reading path; if present it is surfaced only as a
// subtle secondary link at the foot of the page.
//
// Data isolation is enforced server-side: the reader hits
// `GET /api/cruises/current/briefings/{id}` which returns 404 when
// the briefing belongs to a different voyage than the traveller's
// active one.

import React from "react";
import { ArrowLeft, Newspaper, ExternalLink } from "lucide-react";
import { useCruise } from "../../context/CruiseContext";
import { formatCalendarDate, formatCalendarRange, toCalendarDate } from "@/lib/calendarDate";
import { DeltaRibbon, Section, ActionsSection } from "@/components/briefing/BriefingSections";
import { BriefingLongForm } from "@/components/briefing/BriefingLongForm";
import { HomeTimezoneNotice } from "@/components/briefing/HomeTimezoneNotice";
import { BriefingMasthead } from "@/components/briefing/BriefingMedia";
import { useBriefingEnrichment } from "@/components/briefing/useBriefingEnrichment";

const API = process.env.REACT_APP_BACKEND_URL;

// ── Helpers ─────────────────────────────────────────────────
// BR-DATE-003 (2026-02-11 P0) · Never construct `new Date(iso)` from
// a date-only YYYY-MM-DD string — that path treats the value as UTC
// midnight and shifts it backward one calendar day in Western
// timezones. `formatCalendarDate` handles date-only values correctly.
function fmtDate(iso) {
  if (!iso) return "";
  return formatCalendarDate(toCalendarDate(iso), "long") || String(iso);
}

function fmtWeek(iso) {
  if (!iso) return "";
  const d = formatCalendarDate(toCalendarDate(iso), "medium");
  return d ? `Week of ${d}` : "";
}

// Editorial section primitives (DeltaRibbon / Section / ActionsSection)
// are shared with the Briefing Landing via `BriefingSections.jsx`.

// ── Main ────────────────────────────────────────────────────
export default function BriefingReader({ briefingId }) {
  const { selectedCruiseId, setRoute } = useCruise();
  const [state, setState] = React.useState({ loading: true, briefing: null, error: null });
  const [archive, setArchive] = React.useState([]);
  // RC8 · newsletter treatment — shared read-time enrichment (links,
  // thumbnails, port-day weather); identical to the landing panel.
  const enrich = useBriefingEnrichment(selectedCruiseId);

  React.useEffect(() => {
    let cancelled = false;
    setState({ loading: true, briefing: null, error: null });
    // LAW-005 · we hand-append voyage_id here because BriefingReader
    // uses `fetch` directly, not the shared axios interceptor.
    const vParam = selectedCruiseId ? `?voyage_id=${encodeURIComponent(selectedCruiseId)}` : "";
    (async () => {
      try {
        const r = await fetch(`${API}/api/cruises/current/briefings/${briefingId}${vParam}`, {
          credentials: "include",
        });
        if (!r.ok) {
          throw new Error(r.status === 404 ? "Briefing not found" : `Failed to load (${r.status})`);
        }
        const b = await r.json();
        // RC7 · P0 · Voyage-isolation defensive guard (PO 2026-02-11).
        // Server enforces isolation via `_current_voyage_or_404`; the
        // reader adds a client-side belt-and-braces check so a mismatch
        // never displays. If it fires, we log and refuse to render.
        if (selectedCruiseId && b && b.voyage_id && b.voyage_id !== selectedCruiseId) {
           
          console.warn(
            "[BriefingReader] Voyage isolation guard tripped:",
            "expected voyage_id=", selectedCruiseId,
            "received voyage_id=", b.voyage_id,
            "briefing_id=", b.id,
          );
          if (!cancelled) setState({
            loading: false, briefing: null,
            error: "Briefing not found",
          });
          return;
        }
        if (!cancelled) setState({ loading: false, briefing: b, error: null });
      } catch (e) {
        if (!cancelled) setState({ loading: false, briefing: null, error: e.message });
      }
    })();
    // Archive of previous briefings for the voyage
    (async () => {
      try {
        const r = await fetch(`${API}/api/cruises/current/briefings${vParam}`, { credentials: "include" });
        if (!r.ok) return;
        const list = await r.json();
        if (!cancelled && Array.isArray(list)) {
          // Client-side isolation guard on the archive too.
          const scoped = selectedCruiseId
            ? list.filter((x) => !x.voyage_id || x.voyage_id === selectedCruiseId)
            : list;
          setArchive(scoped);
        }
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [briefingId, selectedCruiseId]);

  const goBack = () => setRoute("briefing");

  if (state.loading) {
    return (
      <div className="min-h-screen bg-[#F8F5EE]">
        <div className="max-w-3xl mx-auto px-6 sm:px-10 py-14">
          <div className="h-6 w-24 bg-slate-200 rounded animate-pulse" />
          <div className="mt-8 h-10 w-3/4 bg-slate-200 rounded animate-pulse" />
          <div className="mt-4 h-4 w-1/2 bg-slate-200 rounded animate-pulse" />
        </div>
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="min-h-screen bg-[#F8F5EE]">
        <div className="max-w-3xl mx-auto px-6 sm:px-10 py-14">
          <button
            type="button"
            onClick={goBack}
            className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#334155] hover:text-[#0F172A]"
            data-testid="briefing-back"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Briefing
          </button>
          <div role="alert" data-testid="briefing-not-found" className="mt-12 rounded-2xl border border-slate-200 bg-white px-8 py-10 text-center">
            <Newspaper className="w-8 h-8 mx-auto text-slate-400" />
            <div className="mt-3 font-display text-xl text-[#0F172A]">{state.error}</div>
            <div className="mt-2 text-[13.5px] text-slate-500">
              This briefing may belong to another voyage, or may not yet be published for your current cruise.
            </div>
          </div>
        </div>
      </div>
    );
  }

  const b = state.briefing;
  const ctx = (b.generation_meta && b.generation_meta.context) || {};
  const phaseLabel = (b.generation_meta && b.generation_meta.phase_label) || "";
  const otherArchive = archive.filter((x) => x.id !== b.id);

  // BR-DATE-003 P0 · date-only preservation — use the authoritative
  // embark/disembark from generation_meta.context; render via
  // formatCalendarDate so no UTC-midnight shift can occur.
  const embarkStr = ctx.embark_date ? formatCalendarDate(toCalendarDate(ctx.embark_date), "medium") : "";
  const disembarkStr = ctx.disembark_date ? formatCalendarDate(toCalendarDate(ctx.disembark_date), "medium") : "";
  const dateRange = (embarkStr && disembarkStr) ? `${embarkStr} – ${disembarkStr}` : (embarkStr || "");
  const daysToSail = typeof ctx.days_to_embark === "number" ? ctx.days_to_embark : null;

  return (
    <div className="min-h-screen bg-[#F8F5EE]" data-testid="briefing-reader">
      <div className="max-w-3xl mx-auto px-6 sm:px-10 py-10 sm:py-14">
        {/* Sticky back nav */}
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={goBack}
            className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#334155] hover:text-[#0F172A]"
            data-testid="briefing-back"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Briefing
          </button>
          {phaseLabel && (
            <span className="text-[10.5px] font-semibold uppercase tracking-[0.24em] text-[#C28E42]">
              {phaseLabel}
            </span>
          )}
        </div>

        {/* Editorial hero (P0 · PO 2026-02-11).
            Compact, restrained. Communicates voyage identity + date
            range + countdown before the editorial headline. Feels
            like a premium intelligence publication, not an admin
            report. */}
        <header className="mt-10 border-b border-slate-200 pb-8" data-testid="briefing-hero">
          <div className="text-[10.5px] font-semibold uppercase tracking-[0.28em] text-[#C28E42]">
            Weekly Cruise Intelligence
          </div>
          {ctx.ship_name && (
            <div
              className="mt-3 font-display font-semibold text-[#0F172A] text-[22px] sm:text-[26px] leading-tight tracking-tight"
              data-testid="briefing-hero-ship"
            >
              {ctx.ship_name}
            </div>
          )}
          <div className="mt-1 flex flex-wrap items-baseline gap-x-4 gap-y-1 text-[13px] text-[#475569]"
               data-testid="briefing-hero-meta">
            {ctx.line_name && <span>{ctx.line_name}</span>}
            {dateRange && (
              <>
                {ctx.line_name && <span className="text-slate-300">·</span>}
                <span data-testid="briefing-hero-dates">{dateRange}</span>
              </>
            )}
            {b.published_date && (
              <>
                <span className="text-slate-300">·</span>
                <span>{fmtWeek(b.published_date)}</span>
              </>
            )}
            {daysToSail !== null && daysToSail > 0 && (
              <>
                <span className="text-slate-300">·</span>
                <span className="font-semibold text-[#C28E42]"
                      data-testid="briefing-hero-countdown">
                  {daysToSail} {daysToSail === 1 ? "day" : "days"} to sail
                </span>
              </>
            )}
            {daysToSail !== null && daysToSail === 0 && (
              <>
                <span className="text-slate-300">·</span>
                <span className="font-semibold text-[#C28E42]">Embarks today</span>
              </>
            )}
            {daysToSail !== null && daysToSail < 0 && (
              <>
                <span className="text-slate-300">·</span>
                <span className="font-semibold text-[#C28E42]">In voyage</span>
              </>
            )}
          </div>
          <BriefingMasthead shipId={ctx.ship_id} shipName={ctx.ship_name} />
          <h1 className="mt-6 font-display font-bold text-[#0F172A] text-[28px] sm:text-[36px] leading-[1.1] tracking-tight"
              data-testid="briefing-headline">
            {b.headline || b.title}
          </h1>
          {b.executive_summary && (
            <p className="mt-4 font-display text-[17px] leading-[1.65] text-[#334155]"
               data-testid="briefing-executive-summary">
              {b.executive_summary}
            </p>
          )}
        </header>

        {/* RC7 · Since-Last-Week authoritative-state ribbon.
            Renders before every downstream section. Never fabricated;
            when there's nothing to report it omits itself. */}
        <HomeTimezoneNotice />
        <DeltaRibbon deltas={b.since_last_week} />

        {/* Sections — omitted when empty, EXCEPT What Matters which
            always renders (with a deliberate empty state) because it
            is the traveller's primary decision surface. */}
        <Section eyebrow="What Changed" items={b.what_changed} enrich={enrich} />
        {b.what_matters && b.what_matters.length > 0 ? (
          <Section eyebrow="What Matters" items={b.what_matters} enrich={enrich} />
        ) : (
          <section className="mt-14" data-testid="briefing-section-what-matters">
            <div className="text-[10.5px] font-semibold uppercase tracking-[0.24em] text-[#C28E42]">
              What Matters
            </div>
            <p className="mt-4 text-[15px] leading-[1.7] text-[#475569]"
               data-testid="briefing-what-matters-empty">
              Nothing urgent this week. Your voyage is on track.
            </p>
          </section>
        )}
        <Section eyebrow="What to Watch" items={b.what_to_watch} enrich={enrich} />
        <Section eyebrow="Destination Intelligence" items={b.destination_intelligence} enrich={enrich} />
        <ActionsSection actions={b.voyage_actions} />

        {/* RC8 · Optional authoritative long-form narrative (PO 2026-09-21).
            Rendered only when the edition carries long-form content AND
            the detail endpoint returned the body (list projections strip
            it). Safe Markdown — raw HTML/scripts dropped, links hardened. */}
        {b.long_form && b.long_form.body_markdown && (
          <BriefingLongForm markdown={b.long_form.body_markdown} />
        )}

        {/* Archive rail */}
        {otherArchive.length > 0 && (
          <section className="mt-16 border-t border-slate-200 pt-8" data-testid="briefing-archive">
            <div className="text-[10.5px] font-semibold uppercase tracking-[0.24em] text-[#C28E42]">
              Previous Briefings
            </div>
            <ul className="mt-4 space-y-2">
              {otherArchive.slice(0, 10).map((prev) => (
                <li key={prev.id}>
                  <button
                    type="button"
                    onClick={() => setRoute(`briefing:${prev.id}`)}
                    className="w-full text-left flex items-baseline justify-between gap-4 py-2 hover:text-[#0F172A]"
                    data-testid={`briefing-archive-item-${prev.id}`}
                  >
                    <span className="text-[14px] text-[#334155] group-hover:text-[#0F172A] truncate">
                      {prev.headline || prev.title || "Untitled briefing"}
                    </span>
                    <span className="text-[12px] text-slate-500 shrink-0">
                      {fmtWeek(prev.published_date)}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Optional secondary link to legacy source — never the primary CTA */}
        {b.external_url && (
          <div className="mt-14 border-t border-slate-200 pt-6 text-[12px] text-slate-500">
            <a
              href={b.external_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 hover:text-[#0F172A]"
              data-testid="briefing-external-source"
            >
              View original source
              <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        )}

        {/* Editorial corrections — the edition was amended after publication */}
        {Array.isArray(b.corrections) && b.corrections.length > 0 && (
          <div className="mt-8 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-[12.5px] text-amber-900"
               data-testid="briefing-correction-notice">
            <div className="font-semibold">Correction</div>
            {b.corrections.map((c) => (
              <div key={c.id} data-testid={`briefing-correction-${c.id}`}>
                {c.corrected_at ? `${String(c.corrected_at).slice(0, 10)} · ` : ""}{c.note}
              </div>
            ))}
          </div>
        )}

        {/* Provenance footer — barely-there attribution */}
        {b.generation_meta && (
          <div className="mt-10 text-[10.5px] text-slate-400" data-testid="briefing-provenance-footer">
            Generated by {b.generation_meta.engine || "Cruise Command Briefing Engine"}
            {b.generation_meta.signal_count ? ` · ${b.generation_meta.signal_count} grounded signals` : ""}
          </div>
        )}
      </div>
    </div>
  );
}
